import { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, fetchAuthSession, signOut as amplifySignOut, fetchUserAttributes } from 'aws-amplify/auth';
import { USER_ROLES } from '../config/cognitoConfig';

const rolePriority = [
  USER_ROLES.ADMIN,
  USER_ROLES.RESEARCHER,
  USER_ROLES.CLINICIAN,
  USER_ROLES.COMMUNITY,
  USER_ROLES.PATIENT,
];

function normalizeRole(roleValue) {
  if (!roleValue || typeof roleValue !== 'string') {
    return null;
  }

  const normalized = roleValue.trim().toLowerCase();
  return rolePriority.includes(normalized) ? normalized : null;
}

function resolveRoleFromGroups(groups) {
  if (!groups || groups.length === 0) {
    return null;
  }

  const normalized = groups.map((group) => group.toLowerCase());

  for (const role of rolePriority) {
    if (
      normalized.includes(role)
      || normalized.includes(`mammotrial-${role}s`) // matches mammotrial-patients, mammotrial-clinicians, etc
      || normalized.includes(`role:${role}`)
      || normalized.includes(`portal:${role}`)
    ) {
      return role;
    }
  }

  return null;
}

const AuthContext = createContext(undefined);

// Export AuthContext for testing
export { AuthContext };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [jwtToken, setJwtToken] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [authModal, setAuthModal] = useState({
    open: false,
    mode: 'login', // 'login' or 'signup'
    preSelectedRole: ''
  });

  const loadUser = async () => {
    try {
      setIsLoading(true);
      
      // Get current authenticated user
      const currentUser = await getCurrentUser();
      
      // Fetch user attributes
      const attributes = await fetchUserAttributes();
      const session = await fetchAuthSession();
      const idTokenPayload = session.tokens?.idToken?.payload;
      const tokenGroups = Array.isArray(idTokenPayload?.['cognito:groups'])
        ? idTokenPayload?.['cognito:groups']
        : undefined;
      const idToken = session.tokens?.idToken?.toString?.() || '';

      const roleFromGroups = resolveRoleFromGroups(tokenGroups);
      const roleFromAttributes = normalizeRole(
        attributes['custom:role']
        || attributes['custom:portal']
        || idTokenPayload?.['custom:role']
        || idTokenPayload?.['custom:portal']
      );
      const resolvedRole = roleFromGroups || roleFromAttributes || USER_ROLES.PATIENT;
      
      // Parse user data
      const authUser = {
        username: currentUser.username,
        email: attributes.email || '',
        givenName: attributes.given_name || '',
        familyName: attributes.family_name || '',
        role: resolvedRole,
        sub: attributes.sub || '',
        id: attributes.sub || currentUser.username,
        user_id: attributes.sub || currentUser.username,
        first_name: attributes.given_name || '',
        last_name: attributes.family_name || '',
        primary_role: resolvedRole,
      };
      
      console.log('✅ User loaded successfully:', {
        username: authUser.username,
        email: authUser.email,
        role: authUser.role,
        groups: tokenGroups
      });
      
      setUser(authUser);
      setJwtToken(idToken);
    } catch (error) {
      // User is not authenticated during initial load (expected before login)
      if (error?.name !== 'UserUnAuthenticatedException') {
        console.error('AuthContext - loadUser failed:', error);
      }
      setUser(null);
      setJwtToken('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await amplifySignOut();
      setUser(null);
      setJwtToken('');
      console.log('✅ User signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getTokenForChatbot = async () => {
    const session = await fetchAuthSession();
    return (
      session.tokens?.idToken?.toString?.()
      || session.tokens?.accessToken?.toString?.()
      || ''
    );
  };

  // Modal control functions
  const openAuthModal = (mode = 'login', options = {}) => {
    const { role = '', forceOpen = false } = options;
    
    // Don't open if user is already authenticated unless forced
    if (!!user && !forceOpen) {
      console.log('User already authenticated, skipping auth modal');
      return;
    }
    
    setAuthModal({
      open: true,
      mode: mode === 'signup' || mode === 'register' ? 'signup' : 'login',
      preSelectedRole: role
    });
  };

  const closeAuthModal = () => {
    setAuthModal({
      open: false,
      mode: 'login',
      preSelectedRole: ''
    });
  };

  useEffect(() => {
    loadUser();
    
    // Set up token refresh monitoring
    // Amplify automatically refreshes tokens, but we can listen for auth events
    const checkTokenExpiry = setInterval(async () => {
      try {
        // This will automatically refresh the token if needed
        await fetchAuthSession();
      } catch (error) {
        console.error('Token refresh failed:', error);
        // If refresh fails, user needs to re-authenticate
        setUser(null);
      }
    }, 1000 * 60 * 5); // Check every 5 minutes

    return () => clearInterval(checkTokenExpiry);
  }, []);

  // Get user category for portal routing
  const getUserCategory = () => {
    if (!user || !user.role) {
      return null;
    }

    switch (user.role) {
      case USER_ROLES.PATIENT:
        return 'patient_family';
      case USER_ROLES.CLINICIAN:
      case USER_ROLES.RESEARCHER:
        return 'trials_team';
      case USER_ROLES.COMMUNITY:
        return 'community_resources';
      case USER_ROLES.ADMIN:
        return 'patient_team';
      default:
        return 'patient_family'; // Default fallback
    }
  };

  // Get current portal based on user role
  const getCurrentPortal = () => {
    if (!user || !user.role) {
      return 'patient'; // Default portal
    }

    switch (user.role) {
      case USER_ROLES.PATIENT:
        return 'patient';
      case USER_ROLES.CLINICIAN:
        return 'trials';
      case USER_ROLES.RESEARCHER:
        return 'trials';
      case USER_ROLES.COMMUNITY:
        return 'community';
      case USER_ROLES.ADMIN:
        return 'healthcare';
      default:
        return 'patient'; // Default fallback
    }
  };

  const currentPortal = getCurrentPortal();

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    jwtToken,
    signOut: handleSignOut,
    refreshUser: loadUser,
    getTokenForChatbot,
    openAuthModal,
    closeAuthModal,
    authModal,
    getUserCategory,
    currentPortal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}